import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Button,
    Modal,
    Stack,
    ListItem,
    List,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { Avatar, ListItemButton } from "@mui/joy";
import { useAuth } from "./authContext";
import AuthForm from "./authForm";
import { styled } from '@mui/material/styles';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import LayersIcon from '@mui/icons-material/Layers';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CommentIcon from '@mui/icons-material/Comment';
import WebIcon from '@mui/icons-material/Web';
import CategoryIcon from '@mui/icons-material/Category';
import MultipleStopIcon from '@mui/icons-material/MultipleStop';
import AdminProjectManager from "./AdminProjectsManager";
import CommentAdmin from "./CommentAdmin";
import MapLayerAdmin from "./MapLayerAdmin";
import RoleManagement from "./RoleManagement";
import AdminHome from "./AdminHome";
import FormsAdmin from "./FormsAdmin";
import AdminJoinRequests from "./AdminJoinRequests";
// AdminTaxonomies is the standalone "Themes & Types" admin section. It
// was briefly merged into FormsAdmin as a stacked sub-section (decision
// 4.1, /plan-design-review 2026-05-13) but that layout read as two
// unrelated tools glued together with a divider, so 2026-06-08 promoted
// it back to its own nav item with a single, coherent purpose.
import AdminTaxonomies from "./AdminTaxonomies";
import { apiCall } from './api';
import { hasAnyAdminAccess as computeHasAnyAdminAccess } from './utils/adminAccess';

const AdminDashboard = () => {
    const [showAdminHome, setShowAdminHome] = useState(true);
    const [showLayersAdmin, setShowLayersAdmin] = useState(false);
    const [showFormsAdmin, setShowFormsAdmin] = useState(false);
    const [showCommentAdmin, setShowCommentAdmin] = useState(false);
    const [showProjectManager, setShowProjectManager] = useState(false);
    const { user, loading, logout, globalRoles, projectRoles } = useAuth();
    const [selectedItem, setSelectedItem] = useState(null);
    const [showUserManager, setShowUserManager] = useState(false);
    const [showJoinRequests, setShowJoinRequests] = useState(false);
    const [showTaxonomies, setShowTaxonomies] = useState(false);
    const [pendingJoinCount, setPendingJoinCount] = useState(0);

    const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility

    const isGlobalAdmin = globalRoles && globalRoles.isAdmin;
    const navigate = useNavigate();

    const userProjectIds = React.useMemo(() => {
        if (!projectRoles || projectRoles.length === 0) return [];
        return [...new Set(projectRoles.map(r => {
            const idx = r.lastIndexOf('_');
            return idx > 0 ? r.substring(0, idx) : r;
        }))];
    }, [projectRoles]);

    // hasAnyAdminAccess — true when the caller has admin/mod somewhere.
    // projectRoles is shaped `${projectid}_${PascalCaseRole}` (e.g.
    // "CityOfMadison_Admin") by fetchUserProjectRoles.php. The suffix is
    // PascalCase, so the check is case-insensitive to mirror the server's
    // PROJECT_ADMIN_ROLES (['admin','mod']). See utils/adminAccess.js — the
    // shared helper exists precisely because a lowercase-only compare here
    // used to bounce every project-scoped admin/mod out of /admin (BUG-C).
    const hasAnyAdminAccess = React.useMemo(
        () => computeHasAnyAdminAccess(isGlobalAdmin, projectRoles),
        [isGlobalAdmin, projectRoles],
    );

    // Frontend admin gate. The server is the source of truth (every
    // admin PHP endpoint now calls require_admin_access), so this is
    // pure UX — it stops a signed-in but non-admin user from staring
    // at a broken admin shell where every API call 403s. We wait for
    // `loading` so we don't redirect during the auth boot. Unauthed
    // users still see the login modal (the existing flow), so we only
    // redirect signed-in users who clearly aren't admins.
    useEffect(() => {
        if (loading) return;
        if (!user) return;
        if (!hasAnyAdminAccess) {
            navigate('/', { replace: true });
        }
    }, [loading, user, hasAnyAdminAccess, navigate]);

    const handleLogin = async () => {
        try {
            setIsModalOpen(true);
        } catch (error) {
            console.error('Error opening login form:', error);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false); // Close the modal
    };

    const fetchPendingJoinCount = useCallback(async () => {
        try {
            // Auth refactor 2026-05-27: server now ignores firebase_uid from
            // the body and derives the caller from the verified ID token via
            // apiCall(); the admin scope (global vs project-scoped) is then
            // computed server-side from that uid's role rows.
            const response = await apiCall('joinRequests.php', {
                method: 'POST',
                body: {
                    action: 'list',
                    status: 'pending',
                },
            });
            const pendingCount = Array.isArray(response?.requests)
                ? response.requests.length
                : 0;
            setPendingJoinCount(pendingCount);
        } catch (error) {
            console.error('Error fetching pending join requests:', error);
            setPendingJoinCount(0);
        }
    }, []);

    useEffect(() => {
        if (user) {
            fetchPendingJoinCount();
        }
    }, [user, fetchPendingJoinCount]);




    const drawerWidth = 240;

    const Drawer = styled(MuiDrawer)({
        width: drawerWidth,
        flexShrink: 0,
        boxSizing: 'border-box',
        mt: 10,
        [`& .${drawerClasses.paper}`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
        },
    });
    const mainListItems = [
        { text: 'Home', icon: <HomeRoundedIcon />, stateKey: 'showAdminHome' },
        { text: 'Relocate Markers', icon: <MultipleStopIcon /> },
        { text: 'Project Manager', icon: <WebIcon />, stateKey: 'showProjectManager' },
        { text: 'Comment Manager', icon: <CommentIcon />, stateKey: 'showCommentAdmin' },
        { text: 'Map Layers Manager', icon: <LayersIcon />, stateKey: 'showLayersAdmin' },
        { text: 'Force Update Markers', icon: <LocationOnIcon />, stateKey: 'showUpdateAdmin' },
        { text: 'User Role Management', icon: <PeopleRoundedIcon />, stateKey: 'showUserManager' },
        { text: 'Join Requests', icon: <PeopleRoundedIcon />, stateKey: 'showJoinRequests', notificationCount: pendingJoinCount },
        { text: 'Forms Admin', icon: <WebIcon />, stateKey: 'showFormsAdmin' },
        { text: 'Themes & Types', icon: <CategoryIcon />, stateKey: 'showTaxonomies' },
    ];

    const handleComponentToggle = (stateKey) => {
        // If the selected item is clicked again, close it
        if (selectedItem === stateKey) {
            setSelectedItem(null);
            setShowLayersAdmin(false);
            setShowCommentAdmin(false);
            setShowProjectManager(false);
            setShowUserManager(false);
            setShowFormsAdmin(false);
            setShowJoinRequests(false);
            setShowTaxonomies(false);
            setShowAdminHome(true);
            return;
        }

        setSelectedItem(stateKey); // Set the currently selected item

        // Reset all other states to false
        setShowLayersAdmin(false);
        setShowCommentAdmin(false);
        setShowProjectManager(false);
        setShowUserManager(false);
        setShowAdminHome(false);
        setShowFormsAdmin(false);
        setShowJoinRequests(false);
        setShowTaxonomies(false);

        // Open the selected component
        switch (stateKey) {
            case 'showAdminHome':
                setShowAdminHome(true);
                break;
            case 'showLayersAdmin':
                setShowLayersAdmin(true);
                break;
            case 'showCommentAdmin':
                setShowCommentAdmin(true);
                break;
            case 'showProjectManager':
                setShowProjectManager(true);
                break;
            case 'showUserManager':
                setShowUserManager(true);
                break;
            case 'showFormsAdmin':
                setShowFormsAdmin(true);
                break;
            case 'showJoinRequests':
                setShowJoinRequests(true);
                break;
            case 'showTaxonomies':
                setShowTaxonomies(true);
                break;
            default:
                break;
        }
    };

    return (
        <Box sx={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Modal
                open={isModalOpen}
                onClose={handleCloseModal}
                aria-labelledby="login-modal"
                aria-describedby="login-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '55%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        pt: 0,
                        pb: 4,
                        pr: 4,
                        pl: 4,
                        borderRadius: 2,
                    }}
                >
                    <AuthForm onSuccess={handleCloseModal} />
                </Box>
            </Modal>
            <Box sx={{ height: '90px', backgroundColor: 'black', width: '100vw', position: 'relative' }}>
                <img src={'./LOGO-5.png'} alt="Logo" style={{ height: '50px', position: 'absolute', top: 20, left: 80 }} />
                <Box sx={{ position: 'absolute', left: 350, top: 30 }}>
                    <Typography variant="h5" sx={{ color: "#f5f5f5" }}>Whoseland Mapping Admin Console</Typography>
                </Box>
                <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1000 }}>
                    {loading ? (
                        <Typography>Loading...</Typography>
                    ) : user ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                            <Typography variant="body1" sx={{ marginRight: 2, color: "#f5f5f5" }}>
                                Welcome, {user.displayName || user.email}
                            </Typography>
                            <Button variant="contained" color="secondary" onClick={logout}>
                                Logout
                            </Button>
                        </Box>
                    ) : (
                        <Button variant="contained" color="primary" onClick={handleLogin}>
                            Login
                        </Button>
                    )}
                </Box>
            </Box>
            <Grid container spacing={1} sx={{ width: "100%", height: '100%', overflow: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'absolute', top: 83, left: 0, width: 275, height: "calc(100% - 90px)" }}>
                    <Drawer
                        variant="permanent"
                        sx={{
                            [`& .${drawerClasses.paper}`]: {
                                backgroundColor: 'background.paper',
                                top: 'auto',
                                width: '100%',
                                position: 'relative',
                            },
                            top: 'auto',
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                mt: 'calc(var(--template-frame-height, 0px) + 4px)',
                                p: .25,
                            }}
                        >

                        </Box>
                        <Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
                            <List dense>
                                {mainListItems.map((item, index) => (
                                    <ListItem key={index} sx={{ display: 'block', paddingLeft: 0, paddingRight: 0, paddingBottom: 1, paddingTop: 1 }}>
                                        <ListItemButton
                                            onClick={() => handleComponentToggle(item.stateKey)}
                                            sx={{ backgroundColor: selectedItem === item.stateKey ? 'lightgray' : 'transparent' }}
                                        >
                                            <ListItemIcon>
                                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                    {item.icon}
                                                    {item.notificationCount > 0 && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -2,
                                                                right: -2,
                                                                width: 10,
                                                                height: 10,
                                                                borderRadius: '50%',
                                                                backgroundColor: 'var(--theme-danger-clr)',
                                                                border: '2px solid white',
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.text}
                                                secondary={
                                                    item.notificationCount > 0
                                                        ? `${item.notificationCount} new`
                                                        : undefined
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                            <List dense>
                                <ListItem disablePadding sx={{ display: 'block' }}>
                                    <ListItemButton>
                                        <ListItemIcon><InfoRoundedIcon /></ListItemIcon>
                                        <ListItemText primary='About'></ListItemText>
                                    </ListItemButton>
                                </ListItem>
                            </List>
                        </Stack>
                        <Stack
                            direction="row"
                            sx={{
                                p: 2,
                                gap: 1,
                                alignItems: 'center',
                                borderTop: '1px solid',
                                borderColor: 'divider',
                            }}
                        >

                            <Avatar
                                sizes="small"
                                alt={user?.displayName || 'N/A'}
                                sx={{ width: 36, height: 36 }}
                            />
                            <Box sx={{ mr: 'auto' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: '16px' }}>
                                    {user?.displayName || 'N/A'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {user?.email || 'N/A'}
                                </Typography>
                                {globalRoles.isAdmin === true &&
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        <br /> Website Administrator
                                    </Typography>
                                }
                                {projectRoles && projectRoles.length > 0 &&
                                    projectRoles.map((project, index) => (
                                        <ListItem key={index} disablePadding>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{project}</Typography>
                                        </ListItem>
                                    ))
                                }
                            </Box>
                        </Stack>
                    </Drawer>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', position: 'absolute', top: 83, right: 0, width: "calc(100% - 275px)", height: "calc(100% - 90px)", overflow: 'auto' }}>
                    {showAdminHome && <AdminHome isGlobalAdmin={isGlobalAdmin} userProjectIds={userProjectIds} />}
                    {showLayersAdmin && <MapLayerAdmin isGlobalAdmin={isGlobalAdmin} userProjectIds={userProjectIds} />}
                    {showFormsAdmin && <FormsAdmin isGlobalAdmin={isGlobalAdmin} userProjectIds={userProjectIds} />}
                    {showProjectManager && <AdminProjectManager isGlobalAdmin={isGlobalAdmin} userProjectIds={userProjectIds} />}
                    {showUserManager && <RoleManagement isGlobalAdmin={isGlobalAdmin} userProjectIds={userProjectIds} />}
                    {showJoinRequests && (
                        <AdminJoinRequests
                            onPendingCountChange={setPendingJoinCount}
                            firebaseUid={user?.uid}
                        />
                    )}
                    {showCommentAdmin && <CommentAdmin isGlobalAdmin={isGlobalAdmin} userProjectIds={userProjectIds} />}
                    {showTaxonomies && <AdminTaxonomies isGlobalAdmin={isGlobalAdmin} userProjectIds={userProjectIds} />}
                </Box>
            </Grid>
        </Box>
    );
};

export default AdminDashboard;
